import { debounce } from '../../../CodesThatsGottaGoSomewhereElse';
import { ObjectManager } from '../../../concepts/objects';
import { ActiveObject, resolve } from '../../Container';
import { ConcealmentInfo } from '../../packages/conceal/ConcealmentInfo';
import { IncognitoInfo } from '../../packages/incognito/IncognitoInfo';
import { PopupManager } from '../../popups/PopupManager';
import { EditorSession } from '../EditorSession';
import { MyProfileInfo } from '../MyProfileInfo';
import { MyRoleInfo } from '../MyRoleInfo';
import { ParticipantManager } from '../participants/ParticipantManager';
import { ActiveMeetingInfo, CreateActiveMeetingInfo } from './ActiveMeetingInfo';
import { IsElevated } from './IsElevated';
import { meetingFinishedPopup } from './MeetingFinishedPopup';
import { resolveActiveMeeting, resolveActiveMeetingAndPhase } from './MeetingUtil';

export class MeetingMechanics extends ActiveObject {

    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(ParticipantManager) private participants!: ParticipantManager;
    @resolve(PopupManager) private popups!: PopupManager;
    @resolve(EditorSession) private session!: EditorSession;

    public start() {
        const syncDebounced = debounce(() => this.sync(), 0);
        this.burden(
            this.objects.on('Meeting', 'allocated', syncDebounced),
            this.objects.on('Meeting', 'affected', syncDebounced),
            this.objects.on('Meeting', 'released', syncDebounced),
            this.objects.on('Meeting', 'released', this.invokeMeetingFinishedPopup),
            this.objects.on('Phase', 'allocated', syncDebounced),
            this.objects.on('Phase', 'affected', syncDebounced),
            this.objects.on('Phase', 'released', syncDebounced),
            this.participants.observe(this.setSelfAsHostIfApplicable),
        );
        this.sync();
        this.setSelfAsHostIfApplicable();
    }

    private sync = () => {

        const { canManage } = this.session.get(MyRoleInfo);
        const me = this.session.get(MyProfileInfo);

        // Phase might be null if we are locked (not active)
        const [meeting, phase] = resolveActiveMeetingAndPhase(this.objects);
        const ami = !!meeting ? CreateActiveMeetingInfo(meeting.id, phase?.id || null) : ActiveMeetingInfo.empty;

        this.session.update(ActiveMeetingInfo, ami);
        this.session.patch(ConcealmentInfo, { enabled: phase?.concealment ?? false });
        this.session.patch(IncognitoInfo, { enabled: phase?.incognito ?? false });

        // Am I a host?
        const hosts = meeting?.facilitators ?? [];
        this.session.update(IsElevated, canManage && (hosts.length === 0 || hosts.includes(me.id)));
    };

    private setSelfAsHostIfApplicable = () => {
        const { canParticipate } = this.session.get(MyRoleInfo);
        if (!canParticipate) return;
        const me = this.session.get(MyProfileInfo);
        const ptpts = this.participants.toArray();
        if (ptpts.length !== 1) return;
        if (ptpts[0].id !== me.id) return;
        const meeting = resolveActiveMeeting(this.objects);
        if (!meeting || meeting.facilitators.length === 0) return;
        this.objects.update(() => {
            meeting.facilitators = [me.id];
        });
    };

    private invokeMeetingFinishedPopup = () => {
        this.popups.invoke(meetingFinishedPopup());
    }
}
